import { Component, T_RESPONSE, ModelStrategy } from '../types';
import { ensureQueryLimit, fixScalarSubqueries, convertQuestionsToActions } from './utils';
import { schema } from './schema';
import { promptLoader } from './prompt-loader';
import { LLM } from '../llm';
import { logger } from '../utils/logger';
import { userPromptErrorLogger } from '../utils/user-prompt-error-logger';
import { getCurrentDateTimeForPrompt } from '../utils/datetime';
import type { Action } from '../threads/action';
import KB from './knowledge-base';
import ConversationSearch from './conversation-search';
import { queryCache } from '../utils/query-cache';
// Utilities
import { extractPromptText } from './prompt-extractor';
import { StreamBuffer } from './stream-buffer';
import { processComponentProps } from './utils/component-props-processor';

// Services
import { QueryExecutionService, ToolExecutorService, type ExternalTool } from './services';
import {
	MAX_QUERY_VALIDATION_RETRIES,
	MAX_QUERY_ATTEMPTS,
	MAX_TOKENS_COMPONENT_MATCHING,
	MAX_TOKENS_CLASSIFICATION,
	MAX_TOKENS_ADAPTATION,
	MAX_TOKENS_TEXT_RESPONSE,
	MAX_TOKENS_NEXT_QUESTIONS,
	MAX_AGENT_QUERY_LIMIT,
	EXACT_MATCH_SIMILARITY_THRESHOLD,
	DEFAULT_CONVERSATION_SIMILARITY_THRESHOLD,
	MAX_TOOL_CALLING_ITERATIONS,
	KNOWLEDGE_BASE_TOP_K
} from './constants';

// Re-export ModelStrategy for backward compatibility
export type { ModelStrategy } from '../types';

/**
 * Task types for model selection
 * - 'complex': Text generation, component matching, parameter adaptation (uses best model in balanced mode)
 * - 'simple': Classification, action generation (uses fast model in balanced mode)
 */
export type TaskType = 'complex' | 'simple';

export interface BaseLLMConfig {
	model?: string;
	fastModel?: string;  // Cheaper/faster model for simple tasks (classification, matching, actions)
	defaultLimit?: number;
	apiKey?: string;
	/**
	 * Model selection strategy:
	 * - 'best': Use best model for all tasks (highest quality, higher cost)
	 * - 'fast': Use fast model for all tasks (lower quality, lower cost)
	 * - 'balanced': Use best model for complex tasks, fast model for simple tasks (default)
	 */
	modelStrategy?: ModelStrategy;
	conversationSimilarityThreshold?: number;
}

/**
 * BaseLLM abstract class for AI-powered component generation and matching
 * Provides common functionality for all LLM providers
 */
export abstract class BaseLLM {
	protected model: string;
	protected fastModel: string;  // Used for classification, matching, actions (cheaper)
	protected defaultLimit: number;
	protected apiKey?: string;
	protected modelStrategy: ModelStrategy;
	protected conversationSimilarityThreshold: number;
	protected queryService: QueryExecutionService;

	constructor(config?: BaseLLMConfig) {
		this.model = config?.model || this.getDefaultModel();
		this.fastModel = config?.fastModel || this.getDefaultFastModel();
		this.defaultLimit = config?.defaultLimit || 10;
		this.apiKey = config?.apiKey;
		this.modelStrategy = config?.modelStrategy || 'fast';
		this.conversationSimilarityThreshold = config?.conversationSimilarityThreshold || DEFAULT_CONVERSATION_SIMILARITY_THRESHOLD;

		// Initialize query execution service
		this.queryService = new QueryExecutionService({
			defaultLimit: this.defaultLimit,
			getModelForTask: (taskType) => this.getModelForTask(taskType),
			getApiKey: (apiKey) => this.getApiKey(apiKey),
			providerName: this.getProviderName()
		});
	}

	/**
	 * Get the appropriate model based on task type and model strategy
	 * @param taskType - 'complex' for text generation/matching, 'simple' for classification/actions
	 * @returns The model string to use for this task
	 */
	protected getModelForTask(taskType: TaskType): string {
		switch (this.modelStrategy) {
			case 'best':
				// Use best model for all tasks
				return this.model;
			case 'fast':
				// Use fast model for all tasks
				return this.fastModel;
			case 'balanced':
			default:
				// Use best model for complex tasks, fast model for simple tasks
				return taskType === 'complex' ? this.model : this.fastModel;
		}
	}

	/**
	 * Set the model strategy at runtime
	 * @param strategy - 'best', 'fast', or 'balanced'
	 */
	public setModelStrategy(strategy: ModelStrategy): void {
		this.modelStrategy = strategy;
		logger.info(`[${this.getProviderName()}] Model strategy set to: ${strategy}`);
	}

	/**
	 * Get the current model strategy
	 * @returns The current model strategy
	 */
	public getModelStrategy(): ModelStrategy {
		return this.modelStrategy;
	}


	/**
	 * Set the conversation similarity threshold at runtime
	 * @param threshold - Value between 0 and 1 (e.g., 0.8 = 80% similarity required)
	 */
	public setConversationSimilarityThreshold(threshold: number): void {
		if (threshold < 0 || threshold > 1) {
			logger.warn(`[${this.getProviderName()}] Invalid threshold ${threshold}, must be between 0 and 1. Using default ${DEFAULT_CONVERSATION_SIMILARITY_THRESHOLD}`);
			this.conversationSimilarityThreshold = DEFAULT_CONVERSATION_SIMILARITY_THRESHOLD;
			return;
		}
		this.conversationSimilarityThreshold = threshold;
	}

	/**
	 * Get the current conversation similarity threshold
	 * @returns The current threshold value
	 */
	public getConversationSimilarityThreshold(): number {
		return this.conversationSimilarityThreshold;
	}

	/**
	 * Get the default model for this provider (used for complex tasks like text generation)
	 */
	protected abstract getDefaultModel(): string;

	/**
	 * Get the default fast model for this provider (used for simple tasks: classification, matching, actions)
	 * Should return a cheaper/faster model like Haiku for Anthropic
	 */
	protected abstract getDefaultFastModel(): string;

	/**
	 * Get the default API key from environment
	 */
	protected abstract getDefaultApiKey(): string | undefined;

	/**
	 * Get the provider name (for logging)
	 */
	protected abstract getProviderName(): string;

	/**
	 * Get the API key (from instance, parameter, or environment)
	 */
	protected getApiKey(apiKey?: string): string | undefined {
		return apiKey || this.apiKey || this.getDefaultApiKey();
	}

	/**
	 * Check if a component contains a Form (data_modification component)
	 * Forms have hardcoded defaultValues that become stale when cached
	 * This checks both single Form components and Forms inside MultiComponentContainer
	 */
	protected containsFormComponent(component: any): boolean {
		if (!component) return false;

		// Check if this component itself is a Form
		if (component.type === 'Form' || component.name === 'DynamicForm') {
			return true;
		}

		// Check if this is a Container with nested components
		if (component.type === 'Container' || component.name === 'MultiComponentContainer') {
			const nestedComponents = component.props?.config?.components || [];
			for (const nested of nestedComponents) {
				if (nested.type === 'Form' || nested.name === 'DynamicForm') {
					return true;
				}
			}
		}

		return false;
	}

	/**
	 * Match components from text response suggestions and generate follow-up questions
	 * Takes a text response with component suggestions (c1:type format) and matches with available components
	 * Also generates title, description, and intelligent follow-up questions (actions) based on the analysis
	 * All components are placed in a default MultiComponentContainer layout
	 * @param analysisContent - The text response containing component suggestions
	 * @param components - List of available components
	 * @param apiKey - Optional API key
	 * @param componentStreamCallback - Optional callback to stream primary KPI component as soon as it's identified
	 * @returns Object containing matched components, layout title/description, and follow-up actions
	 */
	async matchComponentsFromAnalysis(
		analysisContent: string,
		components: Component[],
		userPrompt: string,
		apiKey?: string,
		componentStreamCallback?: (component: Component) => void,
		deferredTools?: any[],
		executedTools?: any[],
		collections?: any,
		userId?: string,
	): Promise<{
		components: Component[];
		layoutTitle: string;
		layoutDescription: string;
		actions: Action[];
	}> {
		const methodStartTime = Date.now();
		const methodName = 'matchComponentsFromAnalysis';
		logger.info(`[${this.getProviderName()}] [TIMING] START ${methodName} | model: ${this.getModelForTask('complex')}`);

		try {
			// Format available components for the prompt
			let availableComponentsText = 'No components available';
			if (components && components.length > 0) {
				availableComponentsText = components
					.map((comp, idx) => {
						const keywords = comp.keywords ? comp.keywords.join(', ') : '';
						const propsPreview = comp.props ? JSON.stringify(comp.props, null, 2) : 'No props';
						return `${idx + 1}. ID: ${comp.id}
   Name: ${comp.name}
   Type: ${comp.type}
   Description: ${comp.description || 'No description'}
   Keywords: ${keywords}
   Props Structure: ${propsPreview}`;
					})
					.join('\n\n');
			}

			// Format deferred tools for Form generation
			let deferredToolsText = 'No deferred external tools for this request.';
			if (deferredTools && deferredTools.length > 0) {
				deferredToolsText = 'The following external tools need user input via a Form component.\n' +
					'**IMPORTANT: Use these EXACT values when generating Form externalTool prop.**\n\n' +
					deferredTools.map((tool, idx) => {
						return `${idx + 1}. **${tool.name}**
							toolId: "${tool.id}" (USE THIS EXACT VALUE - do not modify!)
							toolName: "${tool.name}"
							parameters: ${JSON.stringify(tool.params || {})}
							requiredFields:
							${JSON.stringify(tool.requiredFields || [], null, 2)}`;
						}).join('\n\n');
			}

			// Format executed tools for data visualization components
			let executedToolsText = 'No external tools were executed for data fetching.';
			if (executedTools && executedTools.length > 0) {
				executedToolsText = 'The following external tools were executed to fetch data.\n' +
					executedTools.map((tool, idx) => {
						// Format outputSchema if available - this tells LLM exactly what fields are in the result
						let outputSchemaText = 'Not available';
						let fieldNamesList = '';

						// Get record count from formatted result (new structure)
						const recordCount = tool.result?._totalRecords ?? 'unknown';

						// Format metadata if available (e.g., totalItems, totalDeadstockItems)
						let metadataText = '';
						if (tool.result?._metadata && Object.keys(tool.result._metadata).length > 0) {
							const metadataEntries = Object.entries(tool.result._metadata)
								.map(([key, value]) => `${key}: ${value}`)
								.join(', ');
							metadataText = `\n   📋 METADATA: ${metadataEntries}`;
						}

						if (tool.outputSchema) {
							const fields = tool.outputSchema.fields || [];

							// Create a simple list of field names for easy reference
							const numericFields = fields.filter((f: any) => f.type === 'number').map((f: any) => f.name);
							const stringFields = fields.filter((f: any) => f.type === 'string').map((f: any) => f.name);

							fieldNamesList = `
   📊 NUMERIC FIELDS (use for yAxisKey, valueKey, aggregationField): ${numericFields.join(', ') || 'none'}
   📝 STRING FIELDS (use for xAxisKey, groupBy, nameKey): ${stringFields.join(', ') || 'none'}`;

							// Format fields with details
							const fieldsText = fields.map((f: any) =>
								`     "${f.name}" (${f.type}): ${f.description}`
							).join('\n');
							outputSchemaText = `${tool.outputSchema.description}\n   Fields:\n${fieldsText}`;
						}

						return `${idx + 1}. **${tool.name}**
   toolId: "${tool.id}"
   toolName: "${tool.name}"
   parameters: ${JSON.stringify(tool.params || {})}
   recordCount: ${recordCount} rows returned${metadataText}
   outputSchema: ${outputSchemaText}${fieldNamesList}`;
					}).join('\n\n');
			}

			// Get database-specific SQL rules
			const databaseRules = await promptLoader.loadDatabaseRules();

			// Get all knowledge base contexts (global, user-specific, and query-matched)
			let knowledgeBaseContext = 'No additional knowledge base context available.';
			if (collections) {
				const kbResult = await KB.getAllKnowledgeBase({
					prompt: userPrompt || analysisContent,
					collections,
					userId,
					topK: KNOWLEDGE_BASE_TOP_K
				});
				knowledgeBaseContext = kbResult.combinedContext || knowledgeBaseContext;
			}

			// Schema is no longer needed - queries were already executed and schema info is in EXECUTED_TOOLS
			const prompts = await promptLoader.loadPrompts('match-text-components', {
				USER_PROMPT: userPrompt || '',
				ANALYSIS_CONTENT: analysisContent,
				AVAILABLE_COMPONENTS: availableComponentsText,
				SCHEMA_DOC: 'Use column names from executed query results in EXECUTED_TOOLS.',
				DATABASE_RULES: databaseRules,
				DEFERRED_TOOLS: deferredToolsText,
				EXECUTED_TOOLS: executedToolsText,
				KNOWLEDGE_BASE_CONTEXT: knowledgeBaseContext,
				CURRENT_DATETIME: getCurrentDateTimeForPrompt()
			});

			// Log system prompt with clear labeling
			logger.logLLMPrompt('matchComponentsFromAnalysis', 'system', extractPromptText(prompts.system));
			logger.logLLMPrompt('matchComponentsFromAnalysis', 'user', `Text Analysis:\n${analysisContent}\n\nExecuted Tools:\n${executedToolsText}`);

			// Partial JSON streaming to extract answerComponent as soon as it's available
			let fullResponseText = '';
			let answerComponentExtracted = false;

			// Streaming callback to detect and extract answerComponent early
			// Capture componentStreamCallback in closure
			const answerCallback = componentStreamCallback;
			const partialCallback = answerCallback ? (chunk: string) => {
				fullResponseText += chunk;

				// Only try to extract answerComponent if we haven't already and if we have a callback
				if (!answerComponentExtracted && answerCallback) {
					// Check if hasAnswerComponent is true first
					const hasAnswerComponentMatch = fullResponseText.match(/"hasAnswerComponent"\s*:\s*(true|false)/);
					if (!hasAnswerComponentMatch || hasAnswerComponentMatch[1] !== 'true') {
						return; // No answer component or not true yet
					}

					// Look for "answerComponent": { and extract the complete object
					const answerComponentStartMatch = fullResponseText.match(/"answerComponent"\s*:\s*\{/);
					if (!answerComponentStartMatch) {
						return; // answerComponent field not found yet
					}

					// Find the start position of the object
					const startPos = answerComponentStartMatch.index! + answerComponentStartMatch[0].length - 1; // Position of opening {

					// Track brace depth to find the matching closing brace
					let braceDepth = 0;
					let inString = false;
					let escapeNext = false;
					let endPos = -1;

					for (let i = startPos; i < fullResponseText.length; i++) {
						const char = fullResponseText[i];

						if (escapeNext) {
							escapeNext = false;
							continue;
						}

						if (char === '\\') {
							escapeNext = true;
							continue;
						}

						if (char === '"') {
							inString = !inString;
							continue;
						}

						if (!inString) {
							if (char === '{') {
								braceDepth++;
							} else if (char === '}') {
								braceDepth--;
								if (braceDepth === 0) {
									endPos = i + 1;
									break;
								}
							}
						}
					}

					if (endPos > startPos) {
						// We found the complete answerComponent object
						const answerComponentString = fullResponseText.substring(startPos, endPos);

						try {
							const answerComponentData = JSON.parse(answerComponentString);

							if (answerComponentData && answerComponentData.componentId) {
								// Find the component and build it
								const originalComponent = components.find(c => c.id === answerComponentData.componentId);

								if (originalComponent) {
									const answerComponent: Component = {
										...originalComponent,
										props: {
											...originalComponent.props,
											...answerComponentData.props
										}
									};


									// VALIDATE ANSWER COMPONENT QUERY BEFORE STREAMING
									// If the component has a query, validate it first (with retry logic)
									let answerQuery = answerComponent.props?.query;

									// Ensure query limit BEFORE first validation attempt
									if (answerQuery) {
										if (typeof answerQuery === 'string') {
											answerQuery = ensureQueryLimit(answerQuery, this.defaultLimit, MAX_AGENT_QUERY_LIMIT);
										} else if ((answerQuery as any)?.sql) {
											const queryObj = answerQuery as { sql: string; values?: any; params?: any };
											answerQuery = { ...queryObj, sql: ensureQueryLimit(queryObj.sql, this.defaultLimit, MAX_AGENT_QUERY_LIMIT) };
										}
										answerComponent.props.query = answerQuery;
									}

									if (answerQuery && collections?.['database']?.['execute']) {
										// Validate the query asynchronously before streaming (with LLM fix retries)
										(async () => {
											const maxRetries = MAX_QUERY_VALIDATION_RETRIES;
											let attempts = 0;
											let validated = false;
											let currentQuery = answerQuery;
											let currentQueryStr = typeof answerQuery === 'string' ? answerQuery : (answerQuery as any)?.sql || '';
											let lastError = '';


											while (attempts < maxRetries && !validated) {
												attempts++;
												try {
													const cacheKey = this.queryService.getQueryCacheKey(currentQuery);
													if (cacheKey) {
														const result = await collections['database']['execute']({ sql: cacheKey });

														// Cache the result for frontend use
														queryCache.set(cacheKey, result);
														validated = true;

														// Update the answer component with the validated (possibly fixed) query
														if (currentQuery !== answerQuery) {
															answerComponent.props.query = currentQuery;
														}


														// Now stream the validated answer component
														answerCallback(answerComponent);
													}
												} catch (validationError) {
													lastError = validationError instanceof Error ? validationError.message : String(validationError);
													logger.warn(`[${this.getProviderName()}] Answer component query validation failed (attempt ${attempts}/${maxRetries}): ${lastError}`);

													// Try to fix the query using LLM if we have retries left
													if (attempts < maxRetries) {
														try {
															logger.info(`[${this.getProviderName()}] Requesting LLM to fix answer component query...`);
															const fixedQueryStr = await this.queryService.requestQueryFix(
																currentQueryStr,
																lastError,
																{
																	name: answerComponent.name,
																	type: answerComponent.type,
																	title: answerComponent.props?.title
																},
																apiKey
															);

															// Update the query for next attempt (with limit enforcement)
															const limitedFixedQuery = ensureQueryLimit(fixedQueryStr, this.defaultLimit, MAX_AGENT_QUERY_LIMIT);
															if (typeof currentQuery === 'string') {
																currentQuery = limitedFixedQuery;
															} else {
																currentQuery = { ...(currentQuery as any), sql: limitedFixedQuery };
															}
															currentQueryStr = limitedFixedQuery;
															logger.info(`[${this.getProviderName()}] LLM provided fixed query for answer component, retrying...`);
														} catch (fixError) {
															const fixErrorMsg = fixError instanceof Error ? fixError.message : String(fixError);
															logger.error(`[${this.getProviderName()}] Failed to get LLM query fix for answer component: ${fixErrorMsg}`);
															break; // Stop retrying if we can't get a fix
														}
													}
												}
											}

											if (!validated) {
												logger.warn(`[${this.getProviderName()}] Answer component query validation failed after ${attempts} attempts - component will be excluded`);
												// Don't stream - component will be excluded in batch validation
											}
										})();
									} else {
										// No query to validate (e.g., uses externalTool) - stream immediately
										logger.info(`[${this.getProviderName()}] Answer component has no query - STREAMING TO FRONTEND NOW`);
										answerCallback(answerComponent);
									}
									answerComponentExtracted = true;
								}
							}
						} catch (e) {
							// JSON not complete or malformed, will try again with more data
							logger.error(`[${this.getProviderName()}] Partial answerComponent parse failed, waiting for more data...`);
						}
					}
				}
			} : undefined;

			// Let LLM.stream handle JSON parsing automatically, but also use partial callback
		// Use full model for component matching (requires complex SQL generation and prop mapping)
			const result = await LLM.stream<any>(
				{
					sys: prompts.system,
					user: prompts.user
				},
				{
					model: this.getModelForTask('complex'),
					maxTokens: MAX_TOKENS_COMPONENT_MATCHING,
					temperature: 0,
					apiKey: this.getApiKey(apiKey),
					partial: partialCallback
				},
				true // Parse as JSON
			) as any;


			const matchedComponents = result.matchedComponents || [];
			const layoutTitle = result.layoutTitle || 'Dashboard';
			const layoutDescription = result.layoutDescription || 'Multi-component dashboard';

			// Safety fallback: Ensure answerComponent is in matchedComponents
			// The LLM prompt now instructs to include answerComponent as first item in matchedComponents,
			// but this fallback handles cases where LLM doesn't follow instructions
			if (result.hasAnswerComponent && result.answerComponent?.componentId) {
				// Check if first component matches answerComponent (as instructed in prompt)
				const firstComponent = matchedComponents[0];
				const answerComponentStr = JSON.stringify(result.answerComponent);
				const firstComponentStr = firstComponent ? JSON.stringify(firstComponent) : '';

				if (answerComponentStr !== firstComponentStr) {
					logger.info(`[${this.getProviderName()}] Fallback: Adding answerComponent to matchedComponents (LLM didn't include it as first item)`);
					matchedComponents.unshift(result.answerComponent);
				}
			}

			logger.info(`[${this.getProviderName()}] 📦 Matched Components from LLM: ${matchedComponents.length}`);
			matchedComponents.forEach((comp: any, idx: number) => {
				logger.info(`[${this.getProviderName()}]   ${idx + 1}. ${comp.componentType} (${comp.componentName}) - ${comp.originalSuggestion || 'N/A'}`);
			});

			logger.file('\n=============================\nFull LLM response:', JSON.stringify(result, null, 2));
			// Convert question strings to Action objects]
			const rawActions = result.actions || [];
			const actions = convertQuestionsToActions(rawActions);



			// Build final component objects with full metadata
			const finalComponents: Component[] = matchedComponents.map((mc: any) => {
				// Find the original component by name
				const originalComponent = components.find(c => c.name === mc.componentName);

				if (!originalComponent) {
					logger.warn(`[${this.getProviderName()}] Component "${mc.componentName}" not found in available components`);
					return null;
				}

				// Validate and clean up props using the props processor utility
				const cleanedProps = processComponentProps(
					mc.props,
					executedTools,
					{
						providerName: this.getProviderName(),
						defaultLimit: this.defaultLimit
					}
				);

				// Merge generated props with original component
				return {
					...originalComponent,
					props: {
						...originalComponent.props,
						...cleanedProps
					}
				};
			}).filter(Boolean) as Component[];

			// Validate component queries against the database if execute function is available
			let validatedComponents = finalComponents;
			if (collections?.['database']?.['execute']) {

				try {
					const validationResult = await this.queryService.validateComponentQueries(
						finalComponents,
						collections,
						apiKey
					);
					validatedComponents = validationResult.components;

					// Count successes and failures for logging
					const queriedComponents = finalComponents.filter(c => c.props?.query);
					const validatedQueries = validatedComponents.filter(c => c.props?.query);
					logger.info(`[${this.getProviderName()}] Query validation complete: ${validatedQueries.length}/${queriedComponents.length} queries validated`);
				} catch (validationError) {
					const validationErrorMsg = validationError instanceof Error ? validationError.message : String(validationError);
					logger.error(`[${this.getProviderName()}] Query validation error: ${validationErrorMsg}`);
					// Continue with unvalidated components on error
				}
			} else {
				logger.error(`[${this.getProviderName()}] Skipping query validation - database execute function not available`);
			}

			const methodDuration = Date.now() - methodStartTime;
			logger.info(`[${this.getProviderName()}] [TIMING] DONE ${methodName} in ${methodDuration}ms | components: ${validatedComponents.length} | actions: ${actions.length}`);

			return {
				components: validatedComponents,
				layoutTitle,
				layoutDescription,
				actions
			};
		} catch (error) {
			const methodDuration = Date.now() - methodStartTime;
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.error(`[${this.getProviderName()}] [TIMING] FAILED ${methodName} in ${methodDuration}ms | error: ${errorMsg}`);

			// Return empty results on error
			return {
				components: [],
				layoutTitle: 'Dashboard',
				layoutDescription: 'Failed to generate dashboard',
				actions: []
			};
		}
	}

	/**
	 * Classify user question into category and detect external tools needed
	 * Determines if question is for data analysis, requires external tools, or needs text response
	 */
	async classifyQuestionCategory(
		userPrompt: string,
		apiKey?: string,
		conversationHistory?: string,
		externalTools?: any[]
	): Promise<{
		category: 'data_analysis' | 'data_modification' | 'general';
		externalTools: Array<{
			type: string;
			name: string;
			description: string;
			parameters: Record<string, any>;
		}>;
		dataAnalysisType?: 'visualization' | 'calculation' | 'comparison' | 'trend';
		reasoning: string;
		confidence: number;
	}> {
		const methodStartTime = Date.now();
		const methodName = 'classifyQuestionCategory';
		const promptPreview = userPrompt.substring(0, 50) + (userPrompt.length > 50 ? '...' : '');
		logger.info(`[${this.getProviderName()}] [TIMING] START ${methodName} | model: ${this.getModelForTask('simple')} | prompt: "${promptPreview}"`);

		try {
			// Check if any external tool is a database source tool (has 'sql' parameter)
			// Database source tools include their schema in tool descriptions
			const hasDatabaseSourceTool = externalTools?.some(tool => {
				const params = tool.params || {};
				return 'sql' in params || Object.keys(params).some(key => key.toLowerCase() === 'sql');
			}) ?? false;

			// Get database schema documentation - skip if database source tools are available
			// (schema is included in source tool descriptions)
			let schemaDoc: string;
			if (hasDatabaseSourceTool) {
				schemaDoc = 'Schema is available in the database source tool descriptions below.';
			} else {
				schemaDoc = schema.generateSchemaDocumentation();
			}

			// Format available external tools for the system prompt
			const availableToolsDoc = externalTools && externalTools.length > 0
				? externalTools.map(tool => {
					const paramsStr = Object.entries(tool.params || {})
						.map(([key, type]) => `${key}: ${type}`)
						.join(', ');
					return `- **${tool.name}** (id: ${tool.id})\n  Description: ${tool.description}\n  Parameters: ${paramsStr}`;
				}).join('\n\n')
				: 'No external tools available';

			const prompts = await promptLoader.loadPrompts('category-classification', {
				USER_PROMPT: userPrompt,
				CONVERSATION_HISTORY: conversationHistory || 'No previous conversation',
				AVAILABLE_TOOLS: availableToolsDoc,
				SCHEMA_DOC: schemaDoc || 'No database schema available',
				CURRENT_DATETIME: getCurrentDateTimeForPrompt()
			});

			// Log prompts for debugging
			logger.logLLMPrompt('classifyQuestionCategory', 'system', extractPromptText(prompts.system));
			logger.logLLMPrompt('classifyQuestionCategory', 'user', extractPromptText(prompts.user));

			// Use appropriate model based on strategy (simple task)
			const result = await LLM.stream<any>(
				{
					sys: prompts.system,
					user: prompts.user
				},
				{
					model: this.getModelForTask('simple'),
					maxTokens: MAX_TOKENS_CLASSIFICATION,
					temperature: 0,
					apiKey: this.getApiKey(apiKey)
				},
				true // Parse as JSON
			) as any;

			const methodDuration = Date.now() - methodStartTime;
			logger.info(`[${this.getProviderName()}] [TIMING] DONE ${methodName} in ${methodDuration}ms | category: ${result.category} | confidence: ${result.confidence}% | tools: ${(result.externalTools || []).length}`);

			return {
				category: result.category || 'data_analysis',
				externalTools: result.externalTools || [],
				dataAnalysisType: result.dataAnalysisType,
				reasoning: result.reasoning || 'No reasoning provided',
				confidence: result.confidence || 0
			};
		} catch (error) {
			const methodDuration = Date.now() - methodStartTime;
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.error(`[${this.getProviderName()}] [TIMING] FAILED ${methodName} in ${methodDuration}ms | error: ${errorMsg}`);
			throw error;
		}
	}

	/**
	 * Adapt UI block parameters based on current user question
	 * Takes a matched UI block from semantic search and modifies its props to answer the new question
	 * Also adapts the cached text response to match the new question
	 */
	async adaptUIBlockParameters(
		currentUserPrompt: string,
		originalUserPrompt: string,
		matchedUIBlock: any,
		apiKey?: string,
		cachedTextResponse?: string
	): Promise<{
		success: boolean;
		adaptedComponent?: Component;
		adaptedTextResponse?: string;
		parametersChanged?: Array<{ field: string; reason: string }>;
		explanation: string;
	}> {
		const methodStartTime = Date.now();
		const methodName = 'adaptUIBlockParameters';
		const promptPreview = currentUserPrompt.substring(0, 50) + (currentUserPrompt.length > 50 ? '...' : '');
		logger.info(`[${this.getProviderName()}] [TIMING] START ${methodName} | model: ${this.getModelForTask('complex')} | prompt: "${promptPreview}"`);

		try {
			// Support both old format (generatedComponentMetadata) and new format (component)
			const component = matchedUIBlock?.generatedComponentMetadata || matchedUIBlock?.component;

			if (!matchedUIBlock || !component) {
				const methodDuration = Date.now() - methodStartTime;
				logger.info(`[${this.getProviderName()}] [TIMING] DONE ${methodName} in ${methodDuration}ms | result: no component found`);
				return {
					success: false,
					explanation: 'No component found in matched UI block'
				};
			}

		// Get database-specific SQL rules
		const databaseRules = await promptLoader.loadDatabaseRules();

		// Schema is no longer needed - prompt instructs to preserve table/column names from original query
		const prompts = await promptLoader.loadPrompts('adapt-ui-block-params', {
			ORIGINAL_USER_PROMPT: originalUserPrompt,
			CURRENT_USER_PROMPT: currentUserPrompt,
			MATCHED_UI_BLOCK_COMPONENT: JSON.stringify(component, null, 2),
			COMPONENT_PROPS: JSON.stringify(component.props, null, 2),
			CACHED_TEXT_RESPONSE: cachedTextResponse || 'No cached text response available',
			SCHEMA_DOC: 'Preserve table/column names from the original query - they are already validated.',
			DATABASE_RULES: databaseRules,
			CURRENT_DATETIME: getCurrentDateTimeForPrompt()
		});

			const result = await LLM.stream<any>(
				{
					sys: prompts.system,
					user: prompts.user
				},
				{
					model: this.getModelForTask('complex'),
					maxTokens: MAX_TOKENS_ADAPTATION,
					temperature: 0,
					apiKey: this.getApiKey(apiKey)
				},
				true // Parse as JSON
			) as any;

			if (!result.success) {
				const methodDuration = Date.now() - methodStartTime;
				logger.info(`[${this.getProviderName()}] [TIMING] DONE ${methodName} in ${methodDuration}ms | result: adaptation failed - ${result.reason}`);
				return {
					success: false,
					explanation: result.explanation || 'Adaptation not possible'
				};
			}

			// Ensure query has proper LIMIT clause if modified
			if (result.adaptedComponent?.props?.query) {
				result.adaptedComponent.props.query = ensureQueryLimit(
					result.adaptedComponent.props.query,
					this.defaultLimit
				);
			}

			const methodDuration = Date.now() - methodStartTime;
			logger.info(`[${this.getProviderName()}] [TIMING] DONE ${methodName} in ${methodDuration}ms | result: success | changes: ${(result.parametersChanged || []).length}`);

			return {
				success: true,
				adaptedComponent: result.adaptedComponent,
				adaptedTextResponse: result.adaptedTextResponse,
				parametersChanged: result.parametersChanged,
				explanation: result.explanation || 'Parameters adapted successfully'
			};
		} catch (error) {
			const methodDuration = Date.now() - methodStartTime;
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.error(`[${this.getProviderName()}] [TIMING] FAILED ${methodName} in ${methodDuration}ms | error: ${errorMsg}`);
			return {
				success: false,
				explanation: `Error adapting parameters: ${errorMsg}`
			};
		}
	}

	/**
	 * Generate text-based response for user question
	 * This provides conversational text responses instead of component generation
	 * Supports tool calling for query execution with automatic retry on errors (max 3 attempts)
	 * After generating text response, if components are provided, matches suggested components
	 */
	async generateTextResponse(
		userPrompt: string,
		apiKey?: string,
		conversationHistory?: string,
		streamCallback?: (chunk: string) => void,
		collections?: any,
		components?: Component[],
		externalTools?: any[],
		category?: 'data_analysis' | 'data_modification' | 'general',
		userId?: string
	): Promise<T_RESPONSE> {
		const methodStartTime = Date.now();
		const methodName = 'generateTextResponse';
		const promptPreview = userPrompt.substring(0, 50) + (userPrompt.length > 50 ? '...' : '');
		logger.info(`[${this.getProviderName()}] [TIMING] START ${methodName} | model: ${this.getModelForTask('complex')} | category: ${category} | prompt: "${promptPreview}"`);

		const errors: string[] = [];

		try {
			// Step 1: Format available external tools for the system prompt
			// Separate tools by execution type
			let availableToolsDoc = 'No external tools are available for this request.';
			if (externalTools && externalTools.length > 0) {
				logger.info(`[${this.getProviderName()}] External tools available: ${externalTools.map(t => t.name).join(', ')}`);

				// Separate immediate and deferred tools
				const immediateTools = externalTools.filter(t => t.executionType === 'immediate' || (t.executionType === 'deferred' && t.userProvidedData));
				const deferredTools = externalTools.filter(t => t.executionType === 'deferred' && !t.userProvidedData);

				let toolsDocParts: string[] = [];

				// Document IMMEDIATE tools (to be executed)
				if (immediateTools.length > 0) {
					const immediateDoc = '## IMMEDIATE EXECUTION TOOLS\n' +
						'Execute these tools right away:\n\n' +
						immediateTools.map((tool, idx) => {
							const paramsText = Object.entries(tool.params || {})
								.map(([key, value]) => {
									const valueType = typeof value;
									if (valueType === 'string' && ['string', 'number', 'integer', 'boolean', 'array', 'object'].includes(String(value).toLowerCase())) {
										return `- ${key}: ${value}`;
									} else {
										return `- ${key}: ${JSON.stringify(value)} (default value - use this)`;
									}
								})
								.join('\n  ');

							// If deferred tool with user data, include the data
							let userDataText = '';
							if (tool.userProvidedData) {
								userDataText = '\n  **User Provided Data** (use these values):\n  ' +
									Object.entries(tool.userProvidedData)
										.map(([key, value]) => `- ${key}: ${JSON.stringify(value)}`)
										.join('\n  ');
							}

							return `${idx + 1}. **${tool.name}** (ID: ${tool.id})\n  Execution Type: IMMEDIATE\n  Description: ${tool.description}\n  Parameters:\n  ${paramsText}${userDataText}`;
						}).join('\n\n');
					toolsDocParts.push(immediateDoc);
				}

				// Document DEFERRED tools (need user input - will generate Form)
				if (deferredTools.length > 0) {
					const deferredDoc = '## DEFERRED TOOLS (DO NOT EXECUTE)\n' +
						'These tools need user input. A Form component will be generated to collect the data.\n' +
						'**DO NOT call these tools.** Instead, acknowledge the request and inform user that a form will be shown.\n\n' +
						deferredTools.map((tool, idx) => {
							const requiredFieldsText = (tool.requiredFields || [])
								.map((f: any) => `- ${f.label || f.name} (${f.type})${f.required ? ' *required*' : ''}`)
								.join('\n  ');

							return `${idx + 1}. **${tool.name}** (ID: ${tool.id})\n  Execution Type: DEFERRED (needs form input)\n  Description: ${tool.description}\n  Reason: ${tool.executionReason || 'Write operation requires user confirmation'}\n  Required Fields:\n  ${requiredFieldsText || '(fields will be determined by form)'}`;
						}).join('\n\n');
					toolsDocParts.push(deferredDoc);
				}

				availableToolsDoc = toolsDocParts.join('\n\n---\n\n');
			}

			// Check if any external tool is a database source tool (has 'sql' parameter)
			// Database source tools include their schema in tool descriptions
			const hasDatabaseSourceToolForSchema = externalTools?.some(tool => {
				const params = tool.params || {};
				return 'sql' in params || Object.keys(params).some(key => key.toLowerCase() === 'sql');
			}) ?? false;

			// Get schema documentation - skip if database source tools are available
			// (schema is included in source tool descriptions)
			let schemaDoc: string;
			if (hasDatabaseSourceToolForSchema) {
				schemaDoc = 'Schema is available in the database source tool descriptions above.';
				logger.info(`[${this.getProviderName()}] Skipping schema generation (database source tools include schema)`);
			} else {
				schemaDoc = schema.generateSchemaDocumentation();
			}

			// Get database-specific SQL rules
			const databaseRules = await promptLoader.loadDatabaseRules();

			// Get all knowledge base contexts (global, user-specific, and query-matched)
			const kbResult = await KB.getAllKnowledgeBase({
				prompt: userPrompt,
				collections,
				userId,
				topK: KNOWLEDGE_BASE_TOP_K
			});
			const knowledgeBaseContext = kbResult.combinedContext;

			const prompts = await promptLoader.loadPrompts('text-response', {
				USER_PROMPT: userPrompt,
				CONVERSATION_HISTORY: conversationHistory || 'No previous conversation',
				SCHEMA_DOC: schemaDoc,
				DATABASE_RULES: databaseRules,
				KNOWLEDGE_BASE_CONTEXT: knowledgeBaseContext || 'No additional knowledge base context available.',
				AVAILABLE_EXTERNAL_TOOLS: availableToolsDoc,
				CURRENT_DATETIME: getCurrentDateTimeForPrompt()
			});

			// Log prompts for debugging
			logger.logLLMPrompt('generateTextResponse', 'system', extractPromptText(prompts.system));
			logger.logLLMPrompt('generateTextResponse', 'user', extractPromptText(prompts.user));

			// Check if any external tool is a database source tool (has 'sql' parameter)
			// Database source tools include their schema and can execute SQL directly
			const hasDatabaseSourceTool = externalTools?.some(tool => {
				const params = tool.params || {};
				return 'sql' in params || Object.keys(params).some(key => key.toLowerCase() === 'sql');
			}) ?? false;

			// Define tools array
			const tools: any[] = [];

			// Only add execute_query if no database source tools are available
			// This provides backward compatibility while allowing source tools to take over
			if (!hasDatabaseSourceTool) {
				tools.push({
					name: 'execute_query',
					description: 'Executes a parameterized SQL query against the database. CRITICAL: NEVER hardcode literal values in WHERE/HAVING conditions - ALWAYS use $paramName placeholders and pass actual values in params object.',
					input_schema: {
						type: 'object',
						properties: {
							sql: {
								type: 'string',
								description: 'SQL query with $paramName placeholders for ALL literal values in WHERE/HAVING conditions. NEVER hardcode values like WHERE status = \'Delivered\' - instead use WHERE status = $status. Table names, column names, and SQL keywords stay as-is.'
							},
							params: {
								type: 'object',
								description: 'REQUIRED when SQL has WHERE/HAVING conditions. Maps each $paramName placeholder (without $) to its actual value. Pattern: WHERE col = $name → params: { "name": "value" }. Every placeholder in SQL MUST have a corresponding entry here.',
								additionalProperties: true
							},
							reasoning: {
								type: 'string',
								description: 'Brief explanation of what this query does and why it answers the user\'s question.'
							}
						},
						required: ['sql'],
						additionalProperties: false
					}
				});
				logger.info(`[${this.getProviderName()}] Added execute_query tool (no database source tools found)`);
			} else {
				logger.info(`[${this.getProviderName()}] Skipping execute_query tool (database source tool available)`);
			}

			// Add external tools to the tools array (if any were identified by category classification)
			// IMPORTANT: Only add tools that should be executed (immediate OR deferred with user data)
			if (externalTools && externalTools.length > 0) {
				// Filter to only executable tools
				const executableTools = externalTools.filter(t =>
					t.executionType === 'immediate' || (t.executionType === 'deferred' && t.userProvidedData)
				);


				// Track added tool IDs to avoid duplicates (Anthropic requires unique tool names)
				const addedToolIds = new Set<string>();

				executableTools.forEach(tool => {
					// Skip if this tool ID has already been added (same tool called multiple times with different params)
					if (addedToolIds.has(tool.id)) {
						logger.info(`[${this.getProviderName()}] Skipping duplicate tool definition: ${tool.id} (already added)`);
						return;
					}
					addedToolIds.add(tool.id);
					const properties: any = {};
					const required: string[] = [];

					Object.entries(tool.params || {}).forEach(([key, typeOrValue]) => {
						// Normalize type to valid JSON Schema types
						// Handle both type strings and actual values from LLM classification
						let schemaType: string;
						let hasDefaultValue = false;
						let defaultValue: any;

						// Check if this is an actual value (not a type string)
						const valueType = typeof typeOrValue;
						if (valueType === 'number') {
							// If it's a number value, infer the type and mark as having default
							schemaType = Number.isInteger(typeOrValue) ? 'integer' : 'number';
							hasDefaultValue = true;
							defaultValue = typeOrValue;
						} else if (valueType === 'boolean') {
							schemaType = 'boolean';
							hasDefaultValue = true;
							defaultValue = typeOrValue;
						} else if (Array.isArray(typeOrValue)) {
							schemaType = 'array';
							hasDefaultValue = true;
							defaultValue = typeOrValue;
						} else if (valueType === 'object' && typeOrValue !== null) {
							schemaType = 'object';
							hasDefaultValue = true;
							defaultValue = typeOrValue;
						} else {
							// It's a string - could be a type name or a string value
							const typeStr = String(typeOrValue).toLowerCase().trim();

							// Check if it's a known type name
							if (typeStr === 'string' || typeStr === 'str') {
								schemaType = 'string';
								// Type name, no default
							} else if (typeStr === 'number' || typeStr === 'num' || typeStr === 'float' || typeStr === 'double') {
								schemaType = 'number';
								// Type name, no default
							} else if (typeStr === 'integer' || typeStr === 'int') {
								schemaType = 'integer';
								// Type name, no default
							} else if (typeStr === 'boolean' || typeStr === 'bool') {
								schemaType = 'boolean';
								// Type name, no default
							} else if (typeStr === 'array' || typeStr === 'list') {
								schemaType = 'array';
								// Type name, no default
							} else if (typeStr === 'object' || typeStr === 'dict') {
								schemaType = 'object';
								// Type name, no default
							} else {
								// Not a recognized type name, assume it's a string value - infer type as string
								schemaType = 'string';
								hasDefaultValue = true;
								defaultValue = typeOrValue;
							}
						}

						const propertySchema: any = {
							type: schemaType,
							description: `${key} parameter for ${tool.name}`
						};

						// Add default value to schema if present
						if (hasDefaultValue) {
							propertySchema.default = defaultValue;
						} else {
							// Only mark as required if no default value
							required.push(key);
						}

						properties[key] = propertySchema;
					});

					const inputSchema: any = {
						type: 'object',
						properties,
						additionalProperties: false
					};

					// Only include required array if there are required fields
					if (required.length > 0) {
						inputSchema.required = required;
					}

					tools.push({
						name: tool.id,
						description: tool.description,
						input_schema: inputSchema
					});
				});

				logger.info(`[${this.getProviderName()}] Added ${addedToolIds.size} unique tool definitions from ${executableTools.length} tool calls (${externalTools.length - executableTools.length} deferred tools await form input)`);
				// Debug: Log the complete tools array to verify schema
				logger.debug(`[${this.getProviderName()}] Complete tools array:`, JSON.stringify(tools, null, 2));
			}

			// Create stream buffer for smooth text delivery
			const streamBuffer = new StreamBuffer(streamCallback);

			// Create tool executor service
			const toolExecutor = new ToolExecutorService({
				providerName: this.getProviderName(),
				collections,
				streamBuffer
			});

			// Convert externalTools to the expected format
			const executableExternalTools: ExternalTool[] = externalTools?.map(t => ({
				id: t.id,
				name: t.name,
				description: t.description,
				fn: t.fn,
				limit: t.limit,
				outputSchema: t.outputSchema,
				executionType: t.executionType,
				userProvidedData: t.userProvidedData
			})) || [];

			// Create tool handler
			const toolHandler = toolExecutor.createToolHandler(executableExternalTools);

			// Use tool calling with streaming
			// Max iterations needs to account for:
			// - Up to MAX_QUERY_ATTEMPTS query retry attempts
			// - Up to MAX_TOOL_ATTEMPTS external tool retry attempts per tool
			// - 1 additional iteration for final text response
			// - Buffer for multiple tool calls in one iteration
			const result = await LLM.streamWithTools(
				{
					sys: prompts.system,
					user: prompts.user
				},
				tools,
				toolHandler,
				{
					model: this.getModelForTask('complex'),
					maxTokens: MAX_TOKENS_TEXT_RESPONSE,
					temperature: 0,
					apiKey: this.getApiKey(apiKey),
					partial: streamBuffer.hasCallback() ? (chunk: string) => streamBuffer.write(chunk) : undefined
				},
				MAX_TOOL_CALLING_ITERATIONS
			);


			// Use fullStreamedText if available (contains all streamed content), otherwise fallback to result
			const textResponse = streamBuffer.getFullText() || result || 'I apologize, but I was unable to generate a response.';

			// Check if max query attempts were reached
			if (toolExecutor.isMaxAttemptsReached()) {
				const methodDuration = Date.now() - methodStartTime;
				logger.warn(`[${this.getProviderName()}] [TIMING] DONE ${methodName} in ${methodDuration}ms | result: max attempts reached`);

				return {
					success: false,
					errors: [`Maximum query attempts (${MAX_QUERY_ATTEMPTS}) reached. Unable to generate a valid query for your question.`],
					data: {
						text: textResponse,  // Include the streamed text showing all attempts
						actions: [],
						method: `${this.getProviderName()}-text-response-max-attempts`
					}
				};
			}

			// Flush any remaining buffered text before component matching
			streamBuffer.flush();

			// Send marker message to frontend indicating text generation is complete
			// and component matching is starting (skip for general category)
			if (streamBuffer.hasCallback() && components && components.length > 0 && category !== 'general') {
				streamBuffer.write('\n\n📊 **Generating visualization components...**\n\n');
				streamBuffer.write('__TEXT_COMPLETE__COMPONENT_GENERATION_START__');
			}

			// If components are provided, match them from the text response
			// Skip component generation for 'general' category (greetings, small talk, etc.)
			let matchedComponents: Component[] = [];
			let layoutTitle = 'Dashboard';
			let layoutDescription = 'Multi-component dashboard';
			let actions: Action[] = [];

			if (category === 'general') {
				logger.info(`[${this.getProviderName()}] General category - wrapping text response in DynamicMarkdownBlock`);

				// Generate follow-up actions for general questions using text response as context
				const nextQuestions = await this.generateNextQuestions(
					userPrompt,
					null, // no component
					undefined, // no component data
					apiKey,
					conversationHistory,
					textResponse // pass text response as context
				);
				actions = convertQuestionsToActions(nextQuestions);

				// Wrap text response in a DynamicMarkdownBlock component
				// Strip any <DashboardComponents> tags from the content since they're not needed
				const markdownContent = textResponse
					.replace(/<DashboardComponents>[\s\S]*?<\/DashboardComponents>/g, '')
					.trim();

				matchedComponents = [{
					id: 'dynamic-markdown-block',
					name: 'DynamicMarkdownBlock',
					type: 'MarkdownBlock',
					description: 'Text response rendered as markdown',
					props: {
						content: markdownContent
					}
				}];
				layoutTitle = '';
				layoutDescription = '';
			} else if (components && components.length > 0) {

				// Component streaming callback to send answer component immediately to frontend
				// For data_modification, skip answer component streaming - show full dashboard directly
				const componentStreamCallback = (streamBuffer.hasCallback() && category === 'data_analysis') ? (component: Component) => {
					// Send special marker to indicate answer component is ready
					const answerMarker = `__ANSWER_COMPONENT_START__${JSON.stringify(component)}__ANSWER_COMPONENT_END__`;
					streamBuffer.write(answerMarker);
				} : undefined;

				// Extract deferred tools (tools that need user input via Form)
				// For data_modification category, tools with Create/Update/Delete in name are deferred
				const deferredTools = externalTools?.filter(t => {
					// Explicit deferred type
					if (t.executionType === 'deferred' && !t.userProvidedData) return true;
					// Infer deferred for data_modification category based on tool name patterns
					if (category === 'data_modification' && !t.userProvidedData) {
						const name = (t.name || t.id || '').toLowerCase();
						const isWriteOperation = /create|add|insert|new|update|edit|modify|delete|remove|send/.test(name);
						if (isWriteOperation) {
							logger.info(`[${this.getProviderName()}] Inferred deferred execution for tool: ${t.name}`);
							return true;
						}
					}
					return false;
				}) || [];

				if (deferredTools.length > 0) {
					logger.info(`[${this.getProviderName()}] Passing ${deferredTools.length} deferred tools for Form generation`);
				}

				// logger.info(`[${this.getProviderName()}] passing deferred tools to the matching function: ${JSON.stringify(deferredTools,null,2)}`);
				// logger.info(`[${this.getProviderName()}] passing executed tools to the matching function: ${JSON.stringify(executedToolsList,null,2)}`);

				// Sanitize textResponse before passing to component matching LLM
				// Replace <DataTable>JSON</DataTable> with a note to prevent LLM from embedding data directly
				// The actual data was already streamed to the UI, but the component matching LLM should write queries
				// The LLM still has access to: executed SQL query in text, outputSchema from executedTools, and recordCount
				const sanitizedTextResponse = textResponse.replace(
					/<DataTable>[\s\S]*?<\/DataTable>/g,
					'<DataTable>[Data preview removed - for table components, REUSE the exact SQL query shown above (the one that returned these results). Do NOT write a new query or embed data in props.]</DataTable>'
				);

				const matchResult = await this.matchComponentsFromAnalysis(
					sanitizedTextResponse,
					components,
					userPrompt,
					apiKey,
					componentStreamCallback,
					deferredTools,
					toolExecutor.getExecutedTools(),
					collections,
					userId,
				);
				matchedComponents = matchResult.components;
				layoutTitle = matchResult.layoutTitle;
				layoutDescription = matchResult.layoutDescription;
				actions = matchResult.actions;
			}

			let container_componet:Component | null = null;

			if(matchedComponents.length > 0){
				// Create MultiComponentContainer with matched components
				container_componet = {
					id: `container_${Date.now()}`,
					name: 'MultiComponentContainer',
					type: 'Container',
					description: layoutDescription,
					props: {
						config: {
							title: layoutTitle,
							description: layoutDescription,
							components: matchedComponents
						},
						actions: actions
					}
				};
			}

			const methodDuration = Date.now() - methodStartTime;
			logger.info(`[${this.getProviderName()}] [TIMING] DONE ${methodName} in ${methodDuration}ms | result: success | components: ${matchedComponents.length} | actions: ${actions.length}`);

			return {
				success: true,
				data: {
					text: textResponse,
					component: container_componet,
					actions: actions,
					method: `${this.getProviderName()}-text-response`
				},
				errors: []
			};
		} catch (error) {
			const methodDuration = Date.now() - methodStartTime;
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.error(`[${this.getProviderName()}] [TIMING] FAILED ${methodName} in ${methodDuration}ms | error: ${errorMsg}`);

			// Log LLM error with full details
			userPromptErrorLogger.logLlmError(
				this.getProviderName(),
				this.model,
				'generateTextResponse',
				error instanceof Error ? error : new Error(errorMsg),
				{ userPrompt }
			);

			errors.push(errorMsg);

			return {
				success: false,
				errors,
				data: {
					text: 'I apologize, but I encountered an error while processing your question. Please try rephrasing or ask something else.',
					actions: [],
					method: `${this.getProviderName()}-text-response-error`
				}
			};
		}
	}


	/**
	 * Main orchestration function with semantic search and multi-step classification
	 * NEW FLOW (Recommended):
	 * 1. Semantic search: Check previous conversations (>60% match)
	 *    - If match found → Adapt UI block parameters and return
	 * 2. Category classification: Determine if data_analysis, requires_external_tools, or text_response
	 * 3. Route appropriately based on category and response mode
	 */
	async handleUserRequest(
		userPrompt: string,
		components: Component[],
		apiKey?: string,
		conversationHistory?: string,
		responseMode: 'component' | 'text' = 'text',
		streamCallback?: (chunk: string) => void,
		collections?: any,
		externalTools?: any[],
		userId?: string
	): Promise<T_RESPONSE> {
		const startTime = Date.now();

		// Clear log file for new request and log the user prompt
		logger.clearFile();
		logger.logLLMPrompt('handleUserRequest', 'user', `User Prompt: ${userPrompt}`);

		try {
			// STEP 1: Semantic search - Check if this question matches previous conversations (>99% similarity)
			
			// Only use exact matches (>99% similarity) - no semantic threshold matching
			const conversationMatch = await ConversationSearch.searchConversationsWithReranking({
				userPrompt,
				collections,
				userId,
				similarityThreshold: EXACT_MATCH_SIMILARITY_THRESHOLD
			});

			if (conversationMatch) {
				// Found a match! Check if it's a 100% match (exact same question)
				logger.info(`[${this.getProviderName()}] ✓ Found matching conversation with ${(conversationMatch.similarity * 100).toFixed(2)}% similarity`);

				// Extract the component from the UI block
				const rawComponent = conversationMatch.uiBlock?.component || conversationMatch.uiBlock?.generatedComponentMetadata;

				// Check if component is valid (not null, not empty object)
				const isValidComponent = rawComponent && typeof rawComponent === 'object' && Object.keys(rawComponent).length > 0;
				const component = isValidComponent ? rawComponent : null;

				// Extract text response - DB stores as 'analysis'
				const cachedTextResponse = conversationMatch.uiBlock?.analysis || conversationMatch.uiBlock?.textResponse || conversationMatch.uiBlock?.text || '';


				// Check if cached component contains a Form (data_modification)
				// Forms have hardcoded defaultValues that become stale - always fetch fresh data
				if (this.containsFormComponent(component)) {
					logger.info(`[${this.getProviderName()}] Skipping cached result - Form components contain stale defaultValues, fetching fresh data`);
					// Fall through to category classification to generate fresh form with current values
				}
				// Check if this was a general conversation (no component)
				// If so, return text response for exact match or skip adaptation for similar match
				else if (!component) {
					if (conversationMatch.similarity >= EXACT_MATCH_SIMILARITY_THRESHOLD) {
						// Exact match for general question - return cached text response
						const elapsedTime = Date.now() - startTime;
						logger.info(`[${this.getProviderName()}] ✓ Exact match for general question - returning cached text response (${elapsedTime}ms)`);

						return {
							success: true,
							data: {
								text: cachedTextResponse,
								component: null,
								actions: conversationMatch.uiBlock?.actions || [],
								reasoning: `Exact match from previous general conversation`,
								method: `${this.getProviderName()}-semantic-match-general`,
								semanticSimilarity: conversationMatch.similarity
							},
							errors: []
						};
					} else {
						// Similar match but no component (general question) - skip adaptation and process fresh
						logger.info(`[${this.getProviderName()}] Similar match but no component (general question) - processing fresh`);
						// Fall through to category classification
					}
				} else {
					// Has component - proceed with normal flow
					// If similarity >= 99%, it's essentially the same question - return UI block directly
					if (conversationMatch.similarity >= EXACT_MATCH_SIMILARITY_THRESHOLD) {
						const elapsedTime = Date.now() - startTime;
						logger.info(`[${this.getProviderName()}] ✓ 100% match - returning UI block directly without adaptation (${elapsedTime}ms)`);

						// Stream the cached text response to frontend if available
						if (streamCallback && cachedTextResponse) {
							logger.info(`[${this.getProviderName()}] Streaming cached text response to frontend`);
							streamCallback(cachedTextResponse);
						}

						// Extract cached actions from UI block
						const cachedActions = conversationMatch.uiBlock?.actions || [];

						return {
							success: true,
							data: {
								text: cachedTextResponse,
								component: component,
								actions: cachedActions,
								reasoning: `Exact match from previous conversation (${(conversationMatch.similarity * 100).toFixed(2)}% similarity)`,
								method: `${this.getProviderName()}-semantic-match-exact`,
								semanticSimilarity: conversationMatch.similarity
							},
							errors: []
						};
					}

					// Similarity between 60-99%, adapt the UI block parameters
					logger.info(`[${this.getProviderName()}] Adapting parameters for similar question...`);

					// Get the original prompt from the matched UIBlock
					const originalPrompt = conversationMatch.metadata?.userPrompt || 'Previous question';

					// Adapt the UI block parameters to the current question
					const adaptResult = await this.adaptUIBlockParameters(
						userPrompt,
						originalPrompt,
						conversationMatch.uiBlock,
						apiKey,
						cachedTextResponse
					);

					if (adaptResult.success && adaptResult.adaptedComponent) {
						const elapsedTime = Date.now() - startTime;
						logger.info(`[${this.getProviderName()}] ✓ Successfully adapted UI block parameters (${elapsedTime}ms)`);

						// Use adapted text response if available, otherwise fall back to cached
						const textResponseToUse = adaptResult.adaptedTextResponse || cachedTextResponse;

						// Stream the adapted text response to frontend if available
						if (streamCallback && textResponseToUse) {
							logger.info(`[${this.getProviderName()}] Streaming ${adaptResult.adaptedTextResponse ? 'adapted' : 'cached'} text response to frontend`);
							streamCallback(textResponseToUse);
						}

						// Extract cached actions from UI block
						const cachedActions = conversationMatch.uiBlock?.actions || [];

						return {
							success: true,
							data: {
								text: textResponseToUse,
								component: adaptResult.adaptedComponent,
								actions: cachedActions,
								reasoning: `Adapted from previous conversation: ${originalPrompt}`,
								method: `${this.getProviderName()}-semantic-match`,
								semanticSimilarity: conversationMatch.similarity,
								parametersChanged: adaptResult.parametersChanged
							},
							errors: []
						};
					} else {
						logger.info(`[${this.getProviderName()}] Could not adapt matched conversation: ${adaptResult.explanation}, continuing to category classification`);
						// Fall through to category classification
					}
				}
			} else {
				logger.info(`[${this.getProviderName()}] No matching previous conversations found, proceeding to category classification`);
			}

			// STEP 2: Category classification - Determine the type of request
			logger.info(`[${this.getProviderName()}] Step 2: Classifying question category...`);

			const categoryClassification = await this.classifyQuestionCategory(
				userPrompt,
				apiKey,
				conversationHistory,
				externalTools
			);

			logger.info(
				`[${this.getProviderName()}] Question classified as: ${categoryClassification.category} (confidence: ${categoryClassification.confidence}%)`
			);

			// STEP 3: Prepare tools based on category classification
			// Only use tools identified by the LLM classification
			let toolsToUse: any[] = [];
			if (categoryClassification.externalTools && categoryClassification.externalTools.length > 0) {
				logger.info(`[${this.getProviderName()}] Identified ${categoryClassification.externalTools.length} external tools needed: ${categoryClassification.externalTools.map((t: any) => t.name || t.type).join(', ')}`);

				// Log the raw external tools from category classification for debugging
				logger.debug(`[${this.getProviderName()}] Raw external tools from classification: ${JSON.stringify(categoryClassification.externalTools, null, 2)}`);

				// Filter and map only tools that exist in the registered tools list
				// This prevents hallucinated/non-existent tools from being executed
				toolsToUse = categoryClassification.externalTools.reduce<typeof toolsToUse>((acc, t: any) => {
					// Look up the real tool implementation
					const realTool = externalTools?.find(tool => tool.id === t.type);

					// Skip tools that don't exist in registered tools
					if (!realTool) {
						logger.warn(`[${this.getProviderName()}] Tool ${t.type} (${t.name}) not found in registered tools - skipping (likely hallucinated)`);
						return acc;
					}


					acc.push({
						id: t.type,
						name: t.name,
						description: t.description,
						params: t.parameters || {},
						// Include execution type info from category classification
						executionType: t.executionType || 'immediate',
						executionReason: t.executionReason || '',
						requiredFields: t.requiredFields || [],
						userProvidedData: t.userProvidedData || null,
						// Include outputSchema from real tool for component config generation
						outputSchema: realTool.outputSchema,
						fn: realTool.fn
					});
					return acc;
				}, []);

				// Log how many tools were valid vs hallucinated
				const validCount = toolsToUse.length;
				const hallucinatedCount = categoryClassification.externalTools.length - validCount;
				if (hallucinatedCount > 0) {
					logger.warn(`[${this.getProviderName()}] Filtered out ${hallucinatedCount} hallucinated/non-existent tools, ${validCount} valid tools remaining`);
				}
			}

			// STEP 4: Route based on category

			// Generate text response with identified tools
			const textResponse = await this.generateTextResponse(
				userPrompt,
				apiKey,
				conversationHistory,
				streamCallback,
				collections,
				components,
				toolsToUse,
				categoryClassification.category,
				userId
			);

			const elapsedTime = Date.now() - startTime;
			logger.info(`[${this.getProviderName()}] Total time taken: ${elapsedTime}ms (${(elapsedTime / 1000).toFixed(2)}s)`);
			return textResponse;
		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.error(`[${this.getProviderName()}] Error in handleUserRequest: ${errorMsg}`);

			// Log error with full details
			userPromptErrorLogger.logError(
				'handleUserRequest',
				error instanceof Error ? error : new Error(errorMsg),
				{ userPrompt }
			);

			const elapsedTime = Date.now() - startTime;
			logger.info(`[${this.getProviderName()}] Total time taken: ${elapsedTime}ms (${(elapsedTime / 1000).toFixed(2)}s)`);

			return {
				success: false,
				errors: [errorMsg],
				data: {
					text: 'I apologize, but I encountered an error processing your request. Please try again.',
					method: `${this.getProviderName()}-orchestration-error`
				}
			};
		}
	}

	/**
	 * Generate next questions that the user might ask based on the original prompt and generated component
	 * This helps provide intelligent suggestions for follow-up queries
	 * For general/conversational questions without components, pass textResponse instead
	 */
	async generateNextQuestions(
		originalUserPrompt: string,
		component?: Component | null,
		componentData?: Record<string, unknown>,
		apiKey?: string,
		conversationHistory?: string,
		textResponse?: string,
		signal?: AbortSignal,
	): Promise<string[]> {
		const methodStartTime = Date.now();
		const methodName = 'generateNextQuestions';
		const promptPreview = originalUserPrompt.substring(0, 50) + (originalUserPrompt.length > 50 ? '...' : '');
		logger.info(`[${this.getProviderName()}] [TIMING] START ${methodName} | model: ${this.getModelForTask('simple')} | prompt: "${promptPreview}"`);

		try {
			// Build component info - use component if available, otherwise use text response
			let component_info: string;
			if (component) {
				component_info = `
				Component Name: ${component.name}
				Component Type: ${component.type}
				Component Description: ${component.description || 'No description'}
				Component Props: ${component.props ? JSON.stringify(component.props, null, 2) : 'No props'}
			`;
			} else if (textResponse) {
				component_info = `
				Response Type: Text/Conversational Response
				Response Content: ${textResponse.substring(0, 1000)}${textResponse.length > 1000 ? '...' : ''}
			`;
			} else {
				component_info = 'No component or response context available';
			}

			const component_data = componentData ? `Component Data: ${JSON.stringify(componentData, null, 2)}` : '';

			const prompts = await promptLoader.loadPrompts('actions', {
				ORIGINAL_USER_PROMPT: originalUserPrompt,
				COMPONENT_INFO: component_info,
				COMPONENT_DATA: component_data,
				CONVERSATION_HISTORY: conversationHistory || 'No previous conversation',
				CURRENT_DATETIME: getCurrentDateTimeForPrompt()
			});

			// Use appropriate model based on strategy (simple task)
			const result = await LLM.stream<any>(
				{
					sys: prompts.system,
					user: prompts.user
				},
				{
					model: this.getModelForTask('simple'),
					maxTokens: MAX_TOKENS_NEXT_QUESTIONS,
					temperature: 0,
					apiKey: this.getApiKey(apiKey),
					signal,
				},
				true // Parse as JSON
			) as any;

			const nextQuestions = result.nextQuestions || [];

			const methodDuration = Date.now() - methodStartTime;
			logger.info(`[${this.getProviderName()}] [TIMING] DONE ${methodName} in ${methodDuration}ms | questions: ${nextQuestions.length}`);

			return nextQuestions;
		} catch (error) {
			const methodDuration = Date.now() - methodStartTime;
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.error(`[${this.getProviderName()}] [TIMING] FAILED ${methodName} in ${methodDuration}ms | error: ${errorMsg}`);
			// Return empty array on error instead of throwing
			return [];
		}
	}

}
